/**
 * Channels transport boundary.
 *
 * The connector's intake is the subject here: what arrived from a sales
 * channel, which revision of it is retained, what failed and why, and which
 * warehouses a channel may sell from.
 *
 * Two of these operations are privileged in the contract's sense and are
 * treated as such:
 *   • `getChannelOrderRawSnapshot` decrypts a retained source payload, audits
 *     success and denial, answers `Cache-Control: no-store`, and answers 410
 *     once the envelope is purged. Never issued speculatively.
 *   • `putChannelSourceSet` replaces the whole ordered pool; there is no
 *     add-one-source operation, so the editor always sends the complete set.
 *
 * No channel operation is `x-branch-scoped`, so every call here uses the plain
 * http helpers and never sends `X-Acting-Branch-Id`. A channel belongs to the
 * company; switching acting branch must not change what it shows.
 */
import type { components } from "@erp/api-client";

import { apiFetch, type Page } from "@/lib/api/http";
import { toListParams, type CursorQueryArgs, type ListFilters } from "@/features/shared/ops-api";

type Schemas = components["schemas"];

export type ChannelSummary = Schemas["ChannelSummary"];
export type ChannelStatus = Schemas["ChannelStatus"];
export type ChannelSourceSet = Schemas["ChannelSourceSet"];
export type ChannelSourceEntry = Schemas["ChannelSourceEntry"];
export type ChannelOrderSummary = Schemas["ChannelOrderSummary"];
export type ChannelOrderDetail = Schemas["ChannelOrderDetail"];
export type ChannelOrderStatus = Schemas["ChannelOrderStatus"];
export type ChannelOrderRawSnapshot = Schemas["ChannelOrderRawSnapshot"];
export type ChannelOrderRevision = Schemas["ChannelOrderRevision"];
export type ChannelErrorStage = Schemas["ChannelErrorStage"];
export type RetryChannelOrdersAccepted = Schemas["RetryChannelOrdersAccepted"];
export type RetryChannelOrderResult = Schemas["RetryChannelOrderResult"];
export type ReconciliationReport = Schemas["ReconciliationReport"];
export type ReconciliationStatus = Schemas["ReconciliationStatus"];

export const CHANNEL_ORDER_STATUSES: readonly ChannelOrderStatus[] = [
  "pending",
  "imported",
  "error",
];

export const RECONCILIATION_STATUSES: readonly ReconciliationStatus[] = [
  "pending",
  "matched",
  "discrepancy",
  "error",
];

/** One page of a company-scoped list. `fetchPage` from ops-api is deliberately
 *  not used: it sends the acting branch, and none of these routes take one. */
function companyPage<T>(
  path: string,
  filters: ListFilters,
  args: CursorQueryArgs,
): Promise<Page<T>> {
  const { signal, ...paging } = args;
  // Paging spreads last so a filter can never clobber cursor/limit/sort.
  return apiFetch<Page<T>>(path, { query: { ...filters, ...toListParams(paging) }, signal });
}

/** `listChannels` — GET · channels.channel.read. Display lookup only: the
 *  contract never exposes a channel's configuration or credentials. */
export function listChannels(args: CursorQueryArgs = {}): Promise<Page<ChannelSummary>> {
  return companyPage<ChannelSummary>("/channels", {}, args);
}

/** `getChannelSourceSet` — GET · channels.source_set.read. */
export function getChannelSourceSet(
  channelId: string,
  signal?: AbortSignal,
): Promise<ChannelSourceSet> {
  return apiFetch<ChannelSourceSet>(`/channels/${encodeURIComponent(channelId)}/source-set`, {
    signal,
  });
}

/** `putChannelSourceSet` — PUT · channels.source_set.manage. A full ordered
 *  replace: each warehouse at most once, at least one source. */
export function putChannelSourceSet(
  channelId: string,
  sources: ChannelSourceEntry[],
): Promise<ChannelSourceSet> {
  return apiFetch<ChannelSourceSet>(`/channels/${encodeURIComponent(channelId)}/source-set`, {
    method: "PUT",
    body: { sources },
  });
}

export interface ChannelOrderFilters extends ListFilters {
  channel_id?: string;
  status?: ChannelOrderStatus;
  external_id?: string;
  increment_id?: string;
}

/** `listChannelOrders` — GET · channels.order.read. */
export function listChannelOrders(
  filters: ChannelOrderFilters,
  args: CursorQueryArgs = {},
): Promise<Page<ChannelOrderSummary>> {
  return companyPage<ChannelOrderSummary>("/channel-orders", filters, args);
}

/** `getChannelOrder` — GET · channels.order.read. Carries the latest attempt
 *  and the latest revision's metadata; neither history is embedded. */
export function getChannelOrder(
  channelOrderId: string,
  signal?: AbortSignal,
): Promise<ChannelOrderDetail> {
  return apiFetch<ChannelOrderDetail>(`/channel-orders/${encodeURIComponent(channelOrderId)}`, {
    signal,
  });
}

/** `getChannelOrderRawSnapshot` — GET · channels.order.raw.read. Audited on
 *  every call, so it runs only on an explicit operator action. */
export function getChannelOrderRawSnapshot(
  channelOrderId: string,
  revision?: number,
  signal?: AbortSignal,
): Promise<ChannelOrderRawSnapshot> {
  return apiFetch<ChannelOrderRawSnapshot>(
    `/channel-orders/${encodeURIComponent(channelOrderId)}/raw`,
    { query: revision === undefined ? {} : { revision }, signal },
  );
}

/** `retryChannelOrders` — POST · channels.order.retry · client-key. Answers 202
 *  with one disposition per id; "enqueued" is the only one that did anything. */
export function retryChannelOrders(
  ids: string[],
  idempotencyKey: string,
): Promise<RetryChannelOrdersAccepted> {
  return apiFetch<RetryChannelOrdersAccepted>("/channel-orders/retry", {
    method: "POST",
    body: { ids },
    idempotencyKey,
  });
}

export interface ReconciliationFilters extends ListFilters {
  status?: ReconciliationStatus;
  period_from?: string;
  period_to?: string;
  currency?: string;
}

/** `listChannelReconciliationReports` — GET · channels.reconciliation.read. */
export function listReconciliationReports(
  channelId: string,
  filters: ReconciliationFilters,
  args: CursorQueryArgs = {},
): Promise<Page<ReconciliationReport>> {
  return companyPage<ReconciliationReport>(
    `/channels/${encodeURIComponent(channelId)}/reconciliation-reports`,
    filters,
    args,
  );
}

/** `getChannelReconciliationReport` — GET · channels.reconciliation.read. */
export function getReconciliationReport(
  channelId: string,
  reportId: string,
  signal?: AbortSignal,
): Promise<ReconciliationReport> {
  return apiFetch<ReconciliationReport>(
    `/channels/${encodeURIComponent(channelId)}/reconciliation-reports/${encodeURIComponent(reportId)}`,
    { signal },
  );
}
